import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { AlertCircle, CheckCircle, Globe, Search, FileText, Database, Zap, Settings } from "lucide-react";
import SEODailyTracker from './SEODailyTracker';

interface SEOMetadata {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  jsonLdSchema: any;
  robotsMeta: string;
  priority: number;
  changeFreq: string;
}

interface PageType {
  id: string;
  name: string;
  description: string;
  templateCount: number;
  status: 'configured' | 'pending' | 'error';
  lastUpdated: string;
}

const SEOManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState('tracker');
  const [staticSiteEnabled, setStaticSiteEnabled] = useState(false);
  const [autoBuildEnabled, setAutoBuildEnabled] = useState(false);
  const [buildFrequency, setBuildFrequency] = useState('hourly');

  const pageTypes: PageType[] = [
    { id: 'hospitals', name: 'Hospital Pages', description: 'Individual hospital profiles', templateCount: 1247, status: 'configured', lastUpdated: '2025-01-13' },
    { id: 'cpt', name: 'CPT Code Pages', description: 'Current Procedural Terminology codes', templateCount: 8945, status: 'pending', lastUpdated: '2025-01-10' },
    { id: 'drg', name: 'DRG Code Pages', description: 'Diagnosis Related Group codes', templateCount: 745, status: 'pending', lastUpdated: '2025-01-10' },
    { id: 'revenue', name: 'Revenue Code Pages', description: 'Hospital revenue classification codes', templateCount: 2156, status: 'configured', lastUpdated: '2025-01-13' },
    { id: 'supply', name: 'Supply Code Pages', description: 'Medical supply and drug codes', templateCount: 15623, status: 'error', lastUpdated: '2025-01-09' },
    { id: 'rankings', name: 'Hospital Rankings', description: 'Price transparency rankings', templateCount: 1, status: 'configured', lastUpdated: '2025-01-13' },
    { id: 'procedures', name: 'Common Procedures', description: 'Popular medical procedures', templateCount: 156, status: 'pending', lastUpdated: '2025-01-11' }
  ];

  const seoStats = {
    totalPages: 28873,
    indexed: 15234,
    pending: 13639,
    errors: 0,
    avgLoadTime: 1.2,
    mobileScore: 94,
    desktopScore: 98,
    lastBuild: '2025-01-13 14:30:00'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">SEO Management</h2>
          <p className="text-muted-foreground">Optimize search engine visibility and manage static site generation</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => console.log('Generate Sitemap')}>
            <FileText className="w-4 h-4 mr-2" />
            Generate Sitemap
          </Button>
          <Button onClick={() => console.log('Build Static Site')} className="bg-green-600 hover:bg-green-700">
            <Zap className="w-4 h-4 mr-2" />
            Build Static Site
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="tracker">Daily Tracker</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pages">Page Types</TabsTrigger>
          <TabsTrigger value="schemas">JSON-LD</TabsTrigger>
          <TabsTrigger value="static">Static Generation</TabsTrigger>
          <TabsTrigger value="automation">Automation</TabsTrigger>
          <TabsTrigger value="analysis">Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="tracker" className="space-y-6">
          <SEODailyTracker />
        </TabsContent>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Pages</CardTitle>
                <Globe className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{seoStats.totalPages.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Across all page types</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Google Indexed</CardTitle>
                <Search className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{seoStats.indexed.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  {Math.round((seoStats.indexed / seoStats.totalPages) * 100)}% coverage
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Mobile Score</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{seoStats.mobileScore}</div>
                <p className="text-xs text-muted-foreground">PageSpeed Insights</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Load Time</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{seoStats.avgLoadTime}s</div>
                <p className="text-xs text-muted-foreground">Static pages</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>SEO Health Overview</CardTitle>
              <CardDescription>Current status of SEO optimization across all page types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pageTypes.map((pageType) => (
                  <div key={pageType.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <Badge variant={
                        pageType.status === 'configured' ? 'default' :
                        pageType.status === 'pending' ? 'secondary' : 'destructive'
                      }>
                        {pageType.status}
                      </Badge>
                      <div>
                        <h4 className="font-medium">{pageType.name}</h4>
                        <p className="text-sm text-muted-foreground">{pageType.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{pageType.templateCount.toLocaleString()} pages</p>
                      <p className="text-sm text-muted-foreground">Updated {pageType.lastUpdated}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pages" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Page Type SEO Configuration</CardTitle>
              <CardDescription>Configure SEO metadata templates for each page type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <Select defaultValue="hospitals">
                  <SelectTrigger>
                    <SelectValue placeholder="Select page type to configure" />
                  </SelectTrigger>
                  <SelectContent>
                    {pageTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} ({type.templateCount.toLocaleString()} pages)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="title-template">Title Template</Label>
                      <Input 
                        id="title-template" 
                        placeholder="e.g. {{hospital_name}} - Healthcare Price Transparency | Luigi's Hall of Shame"
                        className="font-mono text-sm"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="description-template">Description Template</Label>
                      <Textarea 
                        id="description-template"
                        placeholder="e.g. View {{hospital_name}}'s healthcare pricing data, compare costs across {{procedure_count}} procedures. Price transparency data for {{city}}, {{state}}."
                        className="font-mono text-sm"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="keywords-template">Keywords Template</Label>
                      <Textarea 
                        id="keywords-template"
                        placeholder="e.g. {{hospital_name}}, healthcare costs, medical pricing, {{city}} hospital, price transparency"
                        className="font-mono text-sm"
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="og-title">Open Graph Title</Label>
                      <Input 
                        id="og-title" 
                        placeholder="e.g. {{hospital_name}} Healthcare Pricing Data"
                        className="font-mono text-sm"
                      />
                    </div>

                    <div>
                      <Label htmlFor="og-description">Open Graph Description</Label>
                      <Textarea 
                        id="og-description"
                        placeholder="e.g. Compare healthcare costs at {{hospital_name}}. See pricing for {{procedure_count}} medical procedures in {{city}}, {{state}}."
                        className="font-mono text-sm"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="canonical-url">Canonical URL Pattern</Label>
                      <Input 
                        id="canonical-url" 
                        placeholder="e.g. https://luigishallofshame.com/hospital/{{hospital_id}}"
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button>Save Template</Button>
                  <Button variant="outline">Preview Sample</Button>
                  <Button variant="outline">Test Variables</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schemas" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>JSON-LD Schema Management</CardTitle>
              <CardDescription>Configure structured data schemas for enhanced search results</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Hospital Schema</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Badge variant="default">Organization</Badge>
                        <Badge variant="default">LocalBusiness</Badge>
                        <Badge variant="default">HealthAndBeautyBusiness</Badge>
                        <p className="text-sm text-muted-foreground">Schema for hospital profile pages</p>
                        <Button size="sm" variant="outline" className="w-full">Edit Schema</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Procedure Schema</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Badge variant="default">MedicalProcedure</Badge>
                        <Badge variant="default">Offer</Badge>
                        <Badge variant="default">PriceSpecification</Badge>
                        <p className="text-sm text-muted-foreground">Schema for CPT, DRG, and procedure pages</p>
                        <Button size="sm" variant="outline" className="w-full">Edit Schema</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Rankings Schema</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Badge variant="default">ItemList</Badge>
                        <Badge variant="default">ListItem</Badge>
                        <Badge variant="default">WebPage</Badge>
                        <p className="text-sm text-muted-foreground">Schema for ranking and comparison pages</p>
                        <Button size="sm" variant="outline" className="w-full">Edit Schema</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Schema Editor</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Select defaultValue="hospital">
                        <SelectTrigger>
                          <SelectValue placeholder="Select schema type to edit" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hospital">Hospital Organization Schema</SelectItem>
                          <SelectItem value="procedure">Medical Procedure Schema</SelectItem>
                          <SelectItem value="rankings">Rankings List Schema</SelectItem>
                          <SelectItem value="breadcrumb">Breadcrumb Schema</SelectItem>
                          <SelectItem value="website">Website Schema</SelectItem>
                        </SelectContent>
                      </Select>

                      <Textarea 
                        placeholder={`{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "{{hospital_name}}",
  "description": "{{hospital_description}}",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "{{hospital_address}}",
    "addressLocality": "{{hospital_city}}",
    "addressRegion": "{{hospital_state}}",
    "postalCode": "{{hospital_zip}}"
  },
  "url": "https://luigishallofshame.com/hospital/{{hospital_id}}",
  "sameAs": "{{hospital_website}}"
}`}
                        className="font-mono text-sm"
                        rows={15}
                      />

                      <div className="flex gap-2">
                        <Button>Save Schema</Button>
                        <Button variant="outline">Validate JSON</Button>
                        <Button variant="outline">Test with Google</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="static" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Static Site Generation</CardTitle>
              <CardDescription>Generate database-free static HTML pages for optimal SEO performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Enable Static Site Generation</h4>
                    <p className="text-sm text-muted-foreground">Pre-render all pages as static HTML for search engines</p>
                  </div>
                  <Switch 
                    checked={staticSiteEnabled}
                    onCheckedChange={setStaticSiteEnabled}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium">Build Configuration</h4>
                    
                    <div>
                      <Label htmlFor="output-dir">Output Directory</Label>
                      <Input id="output-dir" value="/static-build" className="font-mono" />
                    </div>

                    <div>
                      <Label htmlFor="base-url">Base URL</Label>
                      <Input id="base-url" value="https://luigishallofshame.com" />
                    </div>

                    <div>
                      <Label htmlFor="concurrent-builds">Concurrent Builds</Label>
                      <Input id="concurrent-builds" type="number" value="10" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium">Page Selection</h4>
                    
                    <div className="space-y-2">
                      {pageTypes.map((type) => (
                        <div key={type.id} className="flex items-center justify-between">
                          <Label htmlFor={`include-${type.id}`} className="text-sm">
                            {type.name} ({type.templateCount.toLocaleString()})
                          </Label>
                          <Switch id={`include-${type.id}`} defaultChecked />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Build Status</h4>
                  <p className="text-sm text-muted-foreground mb-4">Last build: {seoStats.lastBuild}</p>
                  <div className="flex gap-2">
                    <Button>Start Full Build</Button>
                    <Button variant="outline">Incremental Build</Button>
                    <Button variant="outline">Test Build</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="automation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Automated SEO Management</CardTitle>
              <CardDescription>Configure automatic builds, sitemap generation, and SEO monitoring</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Enable Automatic Builds</h4>
                    <p className="text-sm text-muted-foreground">Automatically rebuild static site when data changes</p>
                  </div>
                  <Switch 
                    checked={autoBuildEnabled}
                    onCheckedChange={setAutoBuildEnabled}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium">Build Schedule</h4>
                    
                    <div>
                      <Label htmlFor="build-frequency">Build Frequency</Label>
                      <Select value={buildFrequency} onValueChange={setBuildFrequency}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Every Hour</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="manual">Manual Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="build-time">Preferred Build Time</Label>
                      <Input id="build-time" type="time" value="02:00" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium">Automation Rules</h4>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="auto-sitemap" className="text-sm">Auto-generate sitemap</Label>
                        <Switch id="auto-sitemap" defaultChecked />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="auto-submit" className="text-sm">Submit to Google</Label>
                        <Switch id="auto-submit" defaultChecked />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="auto-monitor" className="text-sm">Monitor indexing status</Label>
                        <Switch id="auto-monitor" defaultChecked />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="auto-cache-clear" className="text-sm">Clear CDN cache</Label>
                        <Switch id="auto-cache-clear" defaultChecked />
                      </div>
                    </div>
                  </div>
                </div>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Build History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-2 border rounded text-sm">
                        <span>Full build completed</span>
                        <span className="text-muted-foreground">2025-01-13 14:30:00</span>
                      </div>
                      <div className="flex justify-between items-center p-2 border rounded text-sm">
                        <span>Incremental build completed</span>
                        <span className="text-muted-foreground">2025-01-13 13:30:00</span>
                      </div>
                      <div className="flex justify-between items-center p-2 border rounded text-sm">
                        <span>Sitemap generated</span>
                        <span className="text-muted-foreground">2025-01-13 14:31:00</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analysis" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>SEO Analysis & Monitoring</CardTitle>
              <CardDescription>Track search performance and identify optimization opportunities</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Google Search Console</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm">Clicks: 45,234</p>
                        <p className="text-sm">Impressions: 234,567</p>
                        <p className="text-sm">CTR: 19.3%</p>
                        <p className="text-sm">Avg Position: 5.2</p>
                        <Button size="sm" variant="outline" className="w-full mt-2">View Details</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Core Web Vitals</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm">LCP: 1.2s ✓</p>
                        <p className="text-sm">FID: 45ms ✓</p>
                        <p className="text-sm">CLS: 0.05 ✓</p>
                        <p className="text-sm">Good URLs: 98%</p>
                        <Button size="sm" variant="outline" className="w-full mt-2">View Report</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Index Coverage</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm">Valid: {seoStats.indexed.toLocaleString()}</p>
                        <p className="text-sm">Pending: {seoStats.pending.toLocaleString()}</p>
                        <p className="text-sm">Errors: 0</p>
                        <p className="text-sm">Coverage: 52.7%</p>
                        <Button size="sm" variant="outline" className="w-full mt-2">Fix Issues</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Keywords</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-2 border rounded text-sm">
                        <span>hospital prices</span>
                        <div className="flex gap-4 text-muted-foreground">
                          <span>Clicks: 2,345</span>
                          <span>Position: 3.2</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-2 border rounded text-sm">
                        <span>medical cost transparency</span>
                        <div className="flex gap-4 text-muted-foreground">
                          <span>Clicks: 1,876</span>
                          <span>Position: 4.1</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-2 border rounded text-sm">
                        <span>healthcare pricing data</span>
                        <div className="flex gap-4 text-muted-foreground">
                          <span>Clicks: 1,654</span>
                          <span>Position: 5.3</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SEOManager;